import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'
import { useDeleteGrowthToolMutation, useGetGrowthToolQuery } from '../../../api/growthTools'
import { NotFound } from '../../../components/ui-kit/NotFound'
import { profileApi } from '../../../api/profile'
import { routesProfile } from '../../../consts'
import { EditGrowthToolTabs } from './Tabs'

export const EditGrowthTool = () => {
  useLayoutEffect(() => {
    document.title = 'Редактирование инструмента личностного роста'
  }, [])

  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [notFound, setNotFound] = useState(false)
  const [noAccess, setNoAccess] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toolId = id ? parseInt(id, 10) : NaN
  const { data, isLoading } = useGetGrowthToolQuery({ id: toolId }, { skip: Number.isNaN(toolId) })
  const { data: me } = profileApi.useGetProfileQuery()
  const [deleteGrowthTool, { isLoading: isDeletingGrowthTool }] = useDeleteGrowthToolMutation()

  const myGrowthToolsPath = useMemo(() => `/${routesProfile.GROWTHTOOLS.path}/mine`, [])

  useEffect(() => {
    if (Number.isNaN(toolId)) {
      setNotFound(true)
      return
    }
    if (!isLoading && !data) {
      setNotFound(true)
      return
    }
    if (data && data.viewer?.permissions?.can_edit_content === false && data.author_id !== me?.id) {
      setNoAccess(true)
    }
  }, [toolId, data, isLoading, me?.id])

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (notFound) {
    return <NotFound text="Инструмент личностного роста не найден" />
  }

  if (noAccess) {
    return <NotFound text="Нет доступа" />
  }

  if (!data) {
    return null
  }

  const canDeleteGrowthTool = data.viewer?.permissions?.can_delete_growth_tool === true

  const handleConfirmDelete = async () => {
    try {
      await deleteGrowthTool({ growthToolId: toolId }).unwrap()
      setIsDeleteModalOpen(false)
      toast.success('Инструмент личностного роста удалён')
      navigate(myGrowthToolsPath, { replace: true })
    } catch (err) {
      const status = (err as { status?: number; originalStatus?: number })?.status
        ?? (err as { originalStatus?: number })?.originalStatus
      if (status === 403) {
        toast.error('Нет прав удалить инструмент личностного роста')
        return
      }
      if (status === 404) {
        toast.error('Инструмент не найден или уже удалён')
        return
      }
      toast.error('Не удалось удалить инструмент личностного роста')
    }
  }

  return (
    <Box
      sx={(theme) => ({
        [theme.breakpoints.down('md')]: {
          paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
        },
      })}
    >
      <EditGrowthToolTabs title="Редактирование инструмента личностного роста" />

      {canDeleteGrowthTool && (
        <>
          <Stack alignItems="center" px={1.5} width="100%" sx={{ mt: 2 }}>
            <Stack width="100%" maxWidth={{ xs: '736px', md: '840px' }}>
              <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
                <Stack gap={1.5}>
                  <Typography variant="black-16-semiBold">Опасная зона</Typography>
                  <Typography variant="black-14-normal" color="text.secondary">
                    Удаление инструмента личностного роста необратимо. Перед удалением убедитесь, что
                    вам больше не нужны его данные.
                  </Typography>
                  <Stack direction="row">
                    <Button variant="danger" onClick={() => setIsDeleteModalOpen(true)}>
                      Удалить инструмент
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          </Stack>

          <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
            <DialogTitle>Удалить инструмент личностного роста</DialogTitle>
            <DialogContent>
              <Typography variant="black-14-normal">
                Инструмент будет удалён и исчезнет из личного кабинета. Действие нельзя отменить.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                variant="follow"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeletingGrowthTool}
              >
                Отмена
              </Button>
              <Button
                variant="danger"
                onClick={() => void handleConfirmDelete()}
                disabled={isDeletingGrowthTool}
              >
                {isDeletingGrowthTool ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} color="inherit" />
                    <span>Удаление...</span>
                  </Stack>
                ) : (
                  'Удалить'
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  )
}
